import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, RefreshCw, ArrowLeft, CheckCircle } from 'lucide-react';
import { LogoIcon } from '../components/Logo';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  
  const user = location.state?.user || JSON.parse(localStorage.getItem('hrms_user') || '{}');
  const email = user?.email || 'your email';

  const handleResendEmail = async () => {
    setResending(true);
    try {
      const token = localStorage.getItem('hrms_token');
      await axios.post(`${API}/auth/resend-verification`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Verification email sent!');
      setResent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('hrms_token');
    localStorage.removeItem('hrms_user');
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 px-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo */}
        <div className="flex items-center justify-center space-x-3 mb-8">
          <LogoIcon className="h-10 w-10" />
          <div className="flex items-baseline space-x-1">
            <span className="text-3xl font-bold text-white">MyHR</span>
            <span className="text-3xl font-bold text-emerald-400">Orbit</span>
          </div>
        </div>

        {/* Card */}
        <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 rounded-3xl p-8 text-center">
          {/* Email Icon */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 flex items-center justify-center">
            <Mail className="w-10 h-10 text-emerald-400" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-3">Verify Your Email</h1>
          
          <p className="text-slate-400 mb-6">
            We've sent a verification email to{' '}
            <span className="text-emerald-400 font-medium">{email}</span>
          </p>

          <p className="text-slate-500 text-sm mb-8">
            Click the link in the email to verify your account and access your dashboard.
            The link will expire in 24 hours.
          </p>

          {/* Actions */}
          <div className="space-y-4">
            <Button
              onClick={handleResendEmail}
              disabled={resending || resent}
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-all"
            >
              {resending ? (
                <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              ) : resent ? (
                <CheckCircle className="w-5 h-5 mr-2" />
              ) : (
                <Mail className="w-5 h-5 mr-2" />
              )}
              {resent ? 'Email Sent!' : 'Resend Verification Email'}
            </Button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center text-slate-400 hover:text-white transition-colors py-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </button>
          </div>

          {/* Tips */}
          <div className="mt-8 pt-6 border-t border-slate-700/50">
            <p className="text-sm text-slate-500">
              Didn't receive the email? Check your spam folder or{' '}
              <button 
                onClick={handleResendEmail}
                disabled={resending}
                className="text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                click here to resend
              </button>
            </p>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          © {new Date().getFullYear()} MyHR Orbit. All rights reserved.
        </p>
      </div>
    </div>
  );
}
